import React, { useEffect, useState } from "react";
import {
  FaEdit,
  FaEye,
  FaPlus,
  FaProjectDiagram,
  FaToggleOff,
  FaToggleOn,
  FaTrash,
} from "react-icons/fa";
import { useNavigate } from "react-router-dom";
import { PermissionGuard } from "../components/PermissionGuard";
import { useAuth } from "../contexts/AuthContext";
import CompactActionBar from "../components/CompactActionBar";
import "../css/standard-components.css";
import {
  Project as ProjectInterface,
  projectService,
} from "../services/projectService";
import { formatDate } from "../utils/formatters";
import { getToken } from "../utils/authStorage";

const Project: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const isSuperAdmin = !!user?.isSuperAdmin;
  const [projects, setProjects] = useState<ProjectInterface[]>([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingProject, setEditingProject] = useState<ProjectInterface | null>(
    null
  );
  const [formData, setFormData] = useState({ name: "" });
  const [filter, setFilter] = useState<"all" | "active" | "inactive">("all");
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [projectToDelete, setProjectToDelete] =
    useState<ProjectInterface | null>(null);
  const [togglingId, setTogglingId] = useState<number | null>(null);
  const [deletingId, setDeletingId] = useState<number | null>(null);
  const [confirmNameInput, setConfirmNameInput] = useState("");

  useEffect(() => {
    fetchProjects();
  }, [filter]);

  const fetchProjects = async () => {
    try {
      setLoading(true);
      const filterValue = filter === "all" ? undefined : filter === "active";
      const data = await projectService.getProjects(filterValue);
      setProjects(data);
    } catch (error) {
      console.error("Error fetching projects:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!formData.name.trim()) {
      alert("Project name is required");
      return;
    }

    try {
      const token = getToken();
      const API_BASE_URL = process.env.REACT_APP_API_URL || "http://localhost:5010/api";
      const url = editingProject
        ? `${API_BASE_URL}/projects/${editingProject.id}`
        : `${API_BASE_URL}/projects`;

      const method = editingProject ? "PUT" : "POST";

      const response = await fetch(url, {
        method,
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify(formData),
      });

      if (response.ok) {
        setShowModal(false);
        setEditingProject(null);
        setFormData({ name: "" });
        fetchProjects();
      } else {
        console.error("Failed to save project");
      }
    } catch (error) {
      console.error("Error saving project:", error);
    }
  };

  const handleEdit = (project: ProjectInterface) => {
    navigate(`/project/edit?id=${project.id}`);
  };

  const handleToggleStatus = async (project: ProjectInterface) => {
    setTogglingId(project.id);
    try {
      const token = getToken();
      const API_BASE_URL = process.env.REACT_APP_API_URL || "http://localhost:5010/api";
      const response = await fetch(
        `${API_BASE_URL}/projects/${project.id}/status`,
        {
          method: "PATCH",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );

      if (response.ok) {
        fetchProjects();
      } else {
        console.error("Failed to toggle project status");
      }
    } catch (error) {
      console.error("Error toggling project status:", error);
    } finally {
      setTogglingId(null);
    }
  };

  const handleDelete = (project: ProjectInterface) => {
    setProjectToDelete(project);
    setConfirmNameInput("");
    setShowDeleteDialog(true);
  };

  const confirmDelete = async () => {
    if (!projectToDelete) return;
    if (confirmNameInput.trim() !== projectToDelete.name) {
      alert("The name you typed does not match the project name.");
      return;
    }

    setDeletingId(projectToDelete.id);
    try {
      const result = await projectService.purgeProject(
        projectToDelete.id,
        confirmNameInput.trim()
      );
      setShowDeleteDialog(false);
      setProjectToDelete(null);
      setConfirmNameInput("");
      const total = Object.values(result.deleted || {}).reduce((a, b) => a + b, 0);
      alert(`${result.message}\n\n${total} related records removed.`);
      fetchProjects();
    } catch (error: any) {
      console.error("Error deleting project:", error);
      const errorMessage =
        error.response?.data?.message ||
        error.response?.data?.error ||
        "Failed to delete project";
      alert(errorMessage);
    } finally {
      setDeletingId(null);
    }
  };

  const cancelDelete = () => {
    setShowDeleteDialog(false);
    setProjectToDelete(null);
    setConfirmNameInput("");
  };

  const openModal = () => {
    setEditingProject(null);
    setFormData({ name: "" });
    setShowModal(true);
  };

  if (loading) {
    return (
      <div className="page-container">
        <div style={{ textAlign: "center", padding: "60px", color: "#6b7280" }}>
          Loading projects...
        </div>
      </div>
    );
  }

  return (
    <div className="page-container">
      <CompactActionBar>
        <div style={{ display: "flex", gap: "12px", alignItems: "center", flex: 1 }}>
          <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
            <span style={{ fontSize: "13px", color: "#6b7280" }}>Status:</span>
            <select
              value={filter}
              onChange={(e) => setFilter(e.target.value as "all" | "active" | "inactive")}
              style={{
                padding: "6px 10px",
                border: "1px solid #e5e7eb",
                borderRadius: "6px",
                fontSize: "13px",
              }}
            >
              <option value="all">All Projects</option>
              <option value="active">Active</option>
              <option value="inactive">Inactive</option>
            </select>
          </div>

          <PermissionGuard permission="project.create">
            <button
              className="btn btn-primary"
              onClick={() => navigate("/project/add")}
              style={{ marginLeft: 'auto', padding: '6px 12px', fontSize: '13px' }}
            >
              <FaPlus /> Add Project
            </button>
          </PermissionGuard>
        </div>
      </CompactActionBar>

      {loading ? (
        <div style={{ background: '#fff', padding: '60px', textAlign: 'center' }}>
          <div style={{fontSize: '16px', color: '#6b7280'}}>Loading projects...</div>
        </div>
      ) : projects.length === 0 ? (
        <div style={{ background: '#fff', padding: '60px', textAlign: 'center' }}>
          <FaProjectDiagram style={{ fontSize: "48px", marginBottom: "16px", opacity: "0.5", color: "#6b7280" }} />
          <h3 style={{ margin: "0 0 8px 0", color: "#374151" }}>No projects found</h3>
          <p style={{ margin: "0 0 24px 0", color: "#6b7280" }}>Get started by creating your first project</p>
          <button className="btn btn-primary" onClick={openModal}>
            <FaPlus /> Create Project
          </button>
        </div>
      ) : (
        <div style={{ background: '#fff', margin: 0, padding: '24px' }}>
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fill, minmax(350px, 1fr))",
              gap: "24px",
            }}
          >
            {projects.map((project) => (
              <div
                key={project.id}
                style={{
                  background: "white",
                  border: "1px solid #e5e7eb",
                  borderRadius: "8px",
                  padding: "24px",
                  boxShadow: "0 1px 3px rgba(0, 0, 0, 0.1)",
                  transition: "all 0.2s ease",
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.boxShadow =
                    "0 4px 6px rgba(0, 0, 0, 0.1)";
                  e.currentTarget.style.transform = "translateY(-2px)";
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.boxShadow =
                    "0 1px 3px rgba(0, 0, 0, 0.1)";
                  e.currentTarget.style.transform = "translateY(0)";
                }}
              >
                <div className="flex justify-between items-start mb-4">
                  <h3
                    style={{
                      margin: 0,
                      fontSize: "18px",
                      fontWeight: "600",
                      color: "#111827",
                    }}
                  >
                    {project.name}
                  </h3>
                  <span
                    className={`status-badge ${
                      project.isActive ? "status-active" : "status-inactive"
                    }`}
                  >
                    {project.isActive ? "Active" : "Inactive"}
                  </span>
                </div>

                <div style={{ marginBottom: "16px" }}>
                  <div
                    style={{
                      background: "#f3f4f6",
                      padding: "12px",
                      borderRadius: "8px",
                      textAlign: "center",
                    }}
                  >
                    <div
                      style={{
                        fontSize: "24px",
                        fontWeight: "700",
                        color: "#4f46e5",
                      }}
                    >
                      {project._count.units}
                    </div>
                    <div
                      style={{
                        fontSize: "12px",
                        color: "#6b7280",
                        textTransform: "uppercase",
                        letterSpacing: "1px",
                      }}
                    >
                      Units
                    </div>
                  </div>
                </div>

                <div style={{ marginBottom: "20px" }}>
                  <div style={{ fontSize: "12px", color: "#6b7280" }}>
                    Created: {formatDate(project.createdAt)}
                  </div>
                </div>

                <div className="table-actions">
                  <PermissionGuard permission="project.view">
                    <button
                      className="btn btn-sm btn-info"
                      onClick={() =>
                        navigate(`/project/units?projectId=${project.id}`)
                      }
                      title="View Units"
                    >
                      <FaEye />
                    </button>
                  </PermissionGuard>
                  <PermissionGuard permission="project.edit">
                    <button
                      className="btn btn-sm btn-primary"
                      onClick={() => handleEdit(project)}
                      title="Edit Project"
                    >
                      <FaEdit />
                    </button>
                  </PermissionGuard>
                  <PermissionGuard permission="project.edit">
                    <button
                      className={`btn btn-sm ${
                        project.isActive ? "btn-warning" : "btn-success"
                      }`}
                      onClick={() => handleToggleStatus(project)}
                      title={project.isActive ? "Deactivate" : "Activate"}
                      disabled={togglingId === project.id}
                    >
                      {togglingId === project.id ? (
                        <span className="spinner-small"></span>
                      ) : project.isActive ? (
                        <FaToggleOff />
                      ) : (
                        <FaToggleOn />
                      )}
                    </button>
                  </PermissionGuard>
                  {isSuperAdmin && (
                    <button
                      className="btn btn-sm btn-danger"
                      onClick={() => handleDelete(project)}
                      title="Delete Project (Super Admin)"
                      disabled={deletingId === project.id}
                    >
                      {deletingId === project.id ? (
                        <span className="spinner-small"></span>
                      ) : (
                        <FaTrash />
                      )}
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {showModal && (
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: "rgba(0, 0, 0, 0.5)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 1000,
          }}
        >
          <div
            style={{
              background: "white",
              borderRadius: "8px",
              padding: "24px",
              width: "90%",
              maxWidth: "500px",
              boxShadow: "0 10px 25px rgba(0, 0, 0, 0.15)",
            }}
          >
            <div className="flex justify-between items-center mb-6">
              <h2
                style={{
                  margin: 0,
                  fontSize: "20px",
                  fontWeight: "600",
                  color: "#111827",
                }}
              >
                {editingProject ? "Edit Project" : "Add New Project"}
              </h2>
              <button
                style={{
                  background: "none",
                  border: "none",
                  fontSize: "24px",
                  cursor: "pointer",
                  color: "#6b7280",
                }}
                onClick={() => setShowModal(false)}
              >
                ×
              </button>
            </div>

            <form onSubmit={handleSubmit}>
              <div className="form-group">
                <label className="form-label" htmlFor="name">
                  Project Name
                </label>
                <input
                  type="text"
                  id="name"
                  className="form-input"
                  value={formData.name}
                  onChange={(e) => setFormData({ name: e.target.value })}
                  placeholder="Enter project name"
                  required
                />
              </div>

              <div className="flex gap-3 justify-end">
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => setShowModal(false)}
                >
                  Cancel
                </button>
                <button type="submit" className="btn btn-primary">
                  {editingProject ? "Update" : "Create"} Project
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {showDeleteDialog && projectToDelete && (
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: "rgba(0,0,0,0.5)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 1000,
          }}
        >
          <div
            style={{
              background: "white",
              borderRadius: "8px",
              padding: "24px",
              width: "90%",
              maxWidth: "520px",
              boxShadow: "0 10px 25px rgba(0,0,0,0.15)",
            }}
          >
            <h2 style={{ margin: "0 0 12px 0", fontSize: "20px", fontWeight: 600, color: "#b91c1c" }}>
              ⚠️ Delete Project — Permanent
            </h2>
            <p style={{ fontSize: "14px", color: "#374151", lineHeight: 1.5 }}>
              This will <strong>permanently delete</strong> the project{" "}
              <strong>"{projectToDelete.name}"</strong> and <strong>everything</strong> tied to it —
              all units, bookings, incomes, receivable (hidden) incomes, expenses, partner incomes,
              cancellations, refunds and transfers — across both databases.
              <br />
              <strong style={{ color: "#b91c1c" }}>This cannot be undone.</strong>
            </p>
            <p style={{ fontSize: "13px", color: "#6b7280", margin: "16px 0 6px" }}>
              To confirm, type the project name exactly: <strong>{projectToDelete.name}</strong>
            </p>
            <input
              type="text"
              className="form-input"
              autoFocus
              value={confirmNameInput}
              onChange={(e) => setConfirmNameInput(e.target.value)}
              placeholder="Type project name to confirm"
              style={{ width: "100%", margin: 0 }}
            />
            <div style={{ display: "flex", gap: "10px", justifyContent: "flex-end", marginTop: "20px" }}>
              <button className="btn btn-secondary" onClick={cancelDelete} disabled={deletingId === projectToDelete.id}>
                Cancel
              </button>
              <button
                className="btn btn-danger"
                onClick={confirmDelete}
                disabled={
                  confirmNameInput.trim() !== projectToDelete.name ||
                  deletingId === projectToDelete.id
                }
              >
                {deletingId === projectToDelete.id ? "Deleting..." : "Delete Everything"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Project;
